//#region react
import React, { useEffect, useState, useMemo, useCallback } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedFlightToView, setSelectedFlightToView } from "_redux/slices/timeline/selected-task";
import { selectAllBookingsByFlight, manualUpdateBookingsTimes, selectManualUpdateBookingsTimesStatus, resetManualUpdateBookingsTimes } from '_redux/slices/timeline/timeline-bookings';
import {
    getFlightPassengerList,
    selectFlightPassengerList,
    selectFlightPassengerListStatus,
    selectFlightEmployeeList,
    reset as resetFlightInformation
} from '_redux/slices/timeline/timeline-flight-information';
import { setShowAddPassenger } from '_redux/slices/timeline/timeline-data';
import { setFlightModal } from '_redux/slices/timeline/timeline-control';
import { selectFirebaseUser, selectIata } from '_redux/slices/main-screen/user';

// components
import { CustomSpinner } from '_components/atoms';
import FlightFeedUpdates from './flight-feed-updates';
import ViewFlightPaxList from './view-flight-pax-list';
import FlightInformationCard from './flight-information-card';
import FlightRouteDisplay from './flight-route-display';
import TabSwitcherSmall from '_components/atoms/tab-switcher-small';
import EmployeeList from './employee-list';

// icons
import { FiRefreshCcw, FiUsers } from "react-icons/fi";
import { IoMdPersonAdd } from 'react-icons/io';
import { MdEditCalendar } from "react-icons/md";

// bootstrap
import { Button } from 'react-bootstrap';

// styles 
import { Spacing, Colors } from '_styles';
import * as TaskColors from '../utils/colors';

//#endregion

// Helper to get header background color based on flight type
const getHeaderBackgroundColor = (flightType) => {
    if (flightType?.toLowerCase() === 'boarding') {
        return TaskColors.combinedBooking;
    } else if (flightType?.toLowerCase() === 'departure') {
        return Colors.PRM_LIGHT_BLUE;
    } else if (flightType?.toLowerCase() === 'arrival') {
        return Colors.ARRIVALS_GREEN;
    }
    return Colors.PRM_LIGHT_BLUE;
};

const ViewFlight = ({ onUpdate, handleGetBookings }) => {

    //#region fetching flight passenger list
    const dispatch = useDispatch()

    const selectedFlightToView = useSelector(selectSelectedFlightToView);
    const [flightKey, setFlightKey] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showUpdateBookings, setShowUpdateBookings] = useState(false);
    // Fallback to cached bookings if fetch fails or is pending
    const cachedBookingsForFlight = useSelector(selectAllBookingsByFlight(flightKey));

    // Flight passenger list from dedicated API (bypasses zone/date filters)
    const fetchedPassengerList = useSelector(selectFlightPassengerList);
    const fetchStatus = useSelector(selectFlightPassengerListStatus);
    const employeeList = useSelector(selectFlightEmployeeList);

    const manualUpdateBookingsTimesStatus = useSelector(selectManualUpdateBookingsTimesStatus);
    const firebaseUser = useSelector(selectFirebaseUser);
    const iata = useSelector(selectIata)

    useEffect(() => {
        const isCancelledArr = (selectedFlightToView?.operationInfo?.status === 'Cancelled' || selectedFlightToView?.operationInfo?.status === 'cancelled') && selectedFlightToView?.type === 'arrival';
        if (isCancelledArr && Array.isFullArray(fetchedPassengerList) && !Array.isFullArray(cachedBookingsForFlight)) {
            setShowUpdateBookings(true);
        }
        else {
            setShowUpdateBookings(false);
        }
    }, [cachedBookingsForFlight, selectedFlightToView, fetchedPassengerList]);
    // set flight key id
    useEffect(() => {

        //console.log('Selected flight to view changed:', selectedFlightToView);

        // check booking id
        if (selectedFlightToView?.bookingKey == null) {
            // no booking key to match with booking
        }
        else {
            // check if boarding included
            if (selectedFlightToView?.type === 'boarding') {
                // remove boarding from type to match with bookings
                setFlightKey(selectedFlightToView?.bookingKey.split('_BOARDING')[0]);
            }
            else {
                // use normal flight key
                console.log('selectedFlightToView?.bookingKey:', selectedFlightToView?.bookingKey);
                setFlightKey(selectedFlightToView?.bookingKey);
            }
        }
    }, [selectedFlightToView]);

    useEffect(() => {
        if (manualUpdateBookingsTimesStatus === 'succeeded') {
            dispatch(resetManualUpdateBookingsTimes());
            handleGetBookings();
        }
    }, [manualUpdateBookingsTimesStatus]);

    // Helper to get flight params for API call
    const getFlightParams = useCallback(() => {

        //console.log('selectedFlightToView:', selectedFlightToView);
        if (!selectedFlightToView) return null;

        const flightNumber = selectedFlightToView?.flightNumber;
        const departureDate = selectedFlightToView?.scheduledDate || selectedFlightToView?.date || selectedFlightToView?.flightDate;

        // Determine the iata (airport code) - use origin for outbound, destination for inbound
        const flightType = selectedFlightToView?.type;
        const isInbound = flightType === 'arrival' || flightType === 'inbound';
        const iata = isInbound
            ? (selectedFlightToView?.arrivalAirport || selectedFlightToView?.destination)
            : (selectedFlightToView?.departureAirport || selectedFlightToView?.origin);

        // Map component flight type to API type
        const apiType = isInbound ? 'inbound' : 'outbound';

        if (flightNumber && departureDate && iata) {
            return { iata, flightNumber, departureDate, type: apiType };
        }
        return null;

    }, [selectedFlightToView]);

    // Fetch passenger list from server
    const fetchPassengerList = useCallback(() => {
        const params = getFlightParams();
        if (params) {
            dispatch(getFlightPassengerList(params));
        }
    }, [dispatch, getFlightParams]);

    // Update last updated timestamp when fetch succeeds
    useEffect(() => {
        if (fetchStatus === 'succeeded') {
            setLastUpdated(new Date());
        }
    }, [fetchStatus]);

    // Fetch passenger list from server when flight changes
    // This bypasses zone and date range filters
    useEffect(() => {
        if (selectedFlightToView && flightKey) {
            fetchPassengerList();
        }

        // Cleanup on unmount
        return () => {
            dispatch(resetFlightInformation());
        };
    }, [dispatch, selectedFlightToView, flightKey, fetchPassengerList]);

    // Refresh handler for manual refresh
    const handleRefresh = useCallback(() => {
        fetchPassengerList();
    }, [fetchPassengerList]);

    const handleAddPax = () => {
        if (!selectedFlightToView) return;

        const processedFlightNumber = selectedFlightToView.bookingKey;
        dispatch(setShowAddPassenger(processedFlightNumber));
        dispatch(setFlightModal(false));
    }

    const handleManualUpdateBookingsTimes = () => {
        if (!selectedFlightToView) return;

        const payload = {
            iata: iata,
            bookingIds: passengerFlightList?.map(booking => booking?._id),
            flightId: selectedFlightToView?._id,
            updatedById: firebaseUser?.uid || firebaseUser?.id || "unknown",
            updatedByName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
        }
        dispatch(manualUpdateBookingsTimes(payload));
    }

    // Use fetched data if available (includes tasks from backend), 
    // otherwise fall back to cached data (without tasks)
    const passengerFlightList = useMemo(() => {
        if (fetchStatus === 'succeeded' && fetchedPassengerList.length > 0) {
            // Backend returns bookings with tasks already combined
            return fetchedPassengerList;
        }
        // Fall back to cached bookings while loading or if fetch returns empty
        // Note: cached bookings may not have tasks attached
        return cachedBookingsForFlight;
    }, [fetchStatus, fetchedPassengerList, cachedBookingsForFlight]);


    //#endregion

    //#region tab state
    const [activeTab, setActiveTab] = useState("passenger-information");

    // tabs configuration
    const tabs = [
        { value: "passenger-information", label: "Passenger Information" },
        { value: "flight-information", label: "Flight Information" },
        { value: "flight-history", label: "Flight History" }
    ];

    // tab change handler
    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
    };
    //#endregion

    return (
        <div style={{ marginBottom: Spacing.SCALE_16, marginTop: -Spacing.SCALE_22 }}>

            {/* modal title - flight number, departure, arrival */}
            <FlightRouteDisplay
                flightNumber={selectedFlightToView?.flightNumber}
                departure={selectedFlightToView?.departureAirport || selectedFlightToView?.origin}
                arrival={selectedFlightToView?.arrivalAirport || selectedFlightToView?.destination}
                flightType={selectedFlightToView?.type}
            />

            {/* Tab Switcher */}
            <TabSwitcherSmall
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                containerStyle={{}}
                tabsListStyle={{
                    marginBottom: Spacing.SCALE_24,
                }}
                tabContentStyle={{}}
            >

                {/* Flight Information Tab */}
                {activeTab === "flight-information" && (
                    <FlightInformationCard
                        flightDocument={selectedFlightToView}
                        onUpdate={onUpdate}
                    />
                )}

                {/* Passenger Information Tab */}
                {activeTab === "passenger-information" && (
                    <>
                        {/* Passenger list header */}
                        <div style={{
                            marginBottom: Spacing.SCALE_12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: getHeaderBackgroundColor(selectedFlightToView?.type),
                            padding: '10px 16px',
                            borderRadius: '8px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                                <FiUsers size={18} color="white" />
                                <span style={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    Passengers ({passengerFlightList?.length || 0})
                                </span>
                            </div>
                            <div style={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: Spacing.SCALE_4,
                            }}>

                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={handleAddPax}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: Spacing.SCALE_4,
                                        padding: '4px 10px',
                                    }}
                                >

                                    <IoMdPersonAdd size={14} />

                                    <span>Add PAX</span>
                                </Button>
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={handleRefresh}
                                    disabled={fetchStatus === 'loading'}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: Spacing.SCALE_4,
                                        padding: '4px 10px',
                                    }}
                                >
                                    {fetchStatus === 'loading' ? (
                                        <CustomSpinner size="small" />
                                    ) : (
                                        <FiRefreshCcw size={14} />
                                    )}
                                    <span>Refresh</span>
                                </Button>
                                {showUpdateBookings && <Button
                                    variant="light"
                                    size="sm"
                                    onClick={handleManualUpdateBookingsTimes}
                                    disabled={manualUpdateBookingsTimesStatus === 'loading'}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: Spacing.SCALE_4,
                                        padding: '4px 10px',
                                    }}
                                >
                                    {manualUpdateBookingsTimesStatus === 'loading' ? (
                                        <CustomSpinner size="small" />
                                    ) : (
                                        <MdEditCalendar size={14} />
                                    )}
                                    <span>Show In Timeline</span>
                                </Button>}
                            </div>
                        </div>

                        {/* Passenger list */}
                        {fetchStatus === 'loading' ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: Spacing.SCALE_24 }}>
                                <CustomSpinner />
                            </div>
                        ) : (
                            <ViewFlightPaxList
                                selectedFlightToView={selectedFlightToView}
                                passengerFlightList={passengerFlightList}
                                flightType={selectedFlightToView?.type}
                                onRefresh={handleRefresh}
                            />
                        )}

                        {/* Employee List */}
                        <EmployeeList
                            employeeList={employeeList}
                            headerColor={getHeaderBackgroundColor(selectedFlightToView?.type)}
                        />
                    </>
                )}

                {/* Flight History Tab */}
                {activeTab === "flight-history" && (
                    <FlightFeedUpdates />
                )}
            </TabSwitcherSmall>
        </div>
    );
};

export default ViewFlight;
